/**
 * FAQ listing behaviour (see sections/jd-faq-listing.liquid):
 *
 *   1. Deep links — `#faq-q-<handle>` opens that question and scrolls to it, from another page
 *      or from a link inside the listing itself (hence the `hashchange` listener; a same-page
 *      anchor never reloads).
 *   2. Active tab — follows the category being read.
 *
 * On (2), this reverses the August decision that made the URL anchor the only source of truth, and
 * the reversal hangs entirely on the tab bar having become sticky (JDTMA-761). While the bar scrolled
 * away with the page it recorded a *choice* — the anchor the visitor had clicked — so any later
 * reading that contradicted it read as a bug, which is why scroll tracking was pulled out. Stuck
 * under the header, the bar is in view for the whole read: it is now a *position indicator*. It
 * therefore has to follow the reading, and the anchor goes back to what it does well — scrolling and
 * opening. Do not re-derive this from the older spec.
 *
 * Three rules decide the active tab, and they are why this reads the rects directly instead of using
 * an IntersectionObserver: expressed as one query they are three lines, whereas a rootMargin band
 * would need rebuilding on resize *and* still need the last two cases special-cased.
 *   - the category whose top was the last to pass under the bar;
 *   - nothing passed yet (top of page) -> the first one, so a bare link to the listing lands on
 *     "Mon compte" already active, as the mockup shows;
 *   - bottom of the document -> the last one, which is otherwise never reached when it is shorter
 *     than the viewport.
 *
 * Without this file the page still works: tabs stay plain anchors, categories are open, and
 * questions toggle natively.
 */
(function () {
  var QUESTION_PREFIX = 'faq-q-';
  /* Long enough for a smooth scroll to land, short enough that a stuck state cannot be felt. Any
     real user gesture cancels the hold before this anyway (see holdDuring). */
  var SCROLL_SETTLE_MS = 700;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Horizon's <accordion-custom> re-asserts `details.open` from its `open-by-default-*`
     attributes on connect and on every breakpoint change. Setting the attributes as well as the
     property makes the opening survive both, whatever order the scripts happen to run in. */
  function openAccordion(details) {
    if (!details) return;
    details.open = true;

    var host = details.closest('accordion-custom');
    if (!host) return;
    host.setAttribute('open-by-default-on-desktop', '');
    host.setAttribute('open-by-default-on-mobile', '');
  }

  /* Works for both anchor shapes: a category section carries the attribute itself, a question
     finds it on an ancestor. Returns null when the hash points outside the FAQ. */
  function categoryHandleFromHash() {
    var hash = window.location.hash.slice(1);
    if (!hash) return null;

    var target = document.getElementById(hash);
    if (!target) return null;

    var category = target.closest('[data-jd-faq-category]');
    return category ? category.dataset.jdFaqCategory : null;
  }

  function revealFromHash() {
    var hash = window.location.hash.slice(1);
    if (!hash || hash.indexOf(QUESTION_PREFIX) !== 0) return;

    var target = document.getElementById(hash);
    if (!target) return;

    var category = target.closest('[data-jd-faq-category]');
    if (category) openAccordion(category.querySelector('.jd-faq-category__details'));
    openAccordion(target.querySelector('.jd-faq-question__details'));

    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  function setupTabs() {
    var tabs = Array.prototype.slice.call(document.querySelectorAll('[data-jd-faq-tab]'));
    if (!tabs.length) return null;

    var tabByHandle = {};
    tabs.forEach(function (tab) {
      tabByHandle[tab.dataset.jdFaqTab] = tab;
    });

    /* Keep the active tab reachable in the mobile scroller — by scrolling that scroller only.
       `scrollIntoView` would walk every scrollable ancestor up to the document, yanking the page
       back up to the tab row every time the active category changes. */
    function keepTabInView(tab) {
      var scroller = tab.closest('[data-jd-faq-tabs]');
      if (!scroller) return;

      var tabBox = tab.getBoundingClientRect();
      var scrollerBox = scroller.getBoundingClientRect();
      /* getBoundingClientRect returns the border box, so aim at the content edges instead:
         resting the tab against the border box would park it under the scroller's own padding. */
      var style = window.getComputedStyle(scroller);
      var visibleStart = scrollerBox.left + parseFloat(style.paddingLeft);
      var visibleEnd = scrollerBox.right - parseFloat(style.paddingRight);
      var delta = 0;

      if (tabBox.left < visibleStart) {
        delta = tabBox.left - visibleStart;
      } else if (tabBox.right > visibleEnd) {
        delta = tabBox.right - visibleEnd;
      }
      if (!delta) return;

      scroller.scrollBy({ left: delta, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    var currentHandle = null;

    return function activate(handle) {
      if (!handle || handle === currentHandle) return;

      currentHandle = handle;
      tabs.forEach(function (tab) {
        if (tab === tabByHandle[handle]) {
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      var active = tabByHandle[handle];
      if (active) keepTabInView(active);
    };
  }

  /* Where the sticky tab bar comes to rest, measured on the header itself.
     `--header-height` cannot be used here: the JD header is custom, and its own script publishes
     `--header-group-height` / `--jd-header-h` but never `--header-height` — that one is only fed by
     Horizon's native `header-component`, which this theme does not render. It therefore keeps the
     literal 60px hardcoded in header-actions.liquid, well short of the real header, and the gap let
     the page scroll through between the two sticky bars.
     Only the section carrying `.jd-header--sticky` stays on screen; the announcement bar above it
     scrolls away, so it must not be counted. `ceil` avoids a sub-pixel hairline. */
  function setupStickyOffset() {
    var listing = document.querySelector('.jd-faq-listing');
    var header = document.querySelector('#header-group .jd-header--sticky');
    var section = header ? header.closest('.shopify-section') : null;
    if (!listing || !section) return;

    function update() {
      var height = Math.ceil(section.getBoundingClientRect().height);
      listing.style.setProperty('--jd-faq-sticky-top', height + 'px');
    }

    update();

    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(section);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }
  }

  function setupScrollSpy(activate) {
    var sections = Array.prototype.slice.call(document.querySelectorAll('[data-jd-faq-category]'));
    var tabsBar = document.querySelector('[data-jd-faq-tabs]');
    if (!sections.length || !tabsBar) return null;

    /* The line the categories are measured against: where the bar comes to rest. Read from the
       element itself rather than recomputed from tokens, so it stays exact across breakpoints,
       font-size settings and browser zoom. */
    function detectionLine() {
      var stuckTop = parseFloat(window.getComputedStyle(tabsBar).top);
      return (isNaN(stuckTop) ? 0 : stuckTop) + tabsBar.offsetHeight;
    }

    /* `scrollY > 0` is not padding: a page short enough to fit the viewport sits at its own end from
       the first paint, and the last tab would light up on arrival instead of the first — measured. */
    function atDocumentEnd() {
      if (!window.scrollY) return false;

      var scrollBottom = window.scrollY + window.innerHeight;
      return scrollBottom >= document.documentElement.scrollHeight - 2;
    }

    function handleInView() {
      if (atDocumentEnd()) return sections[sections.length - 1].dataset.jdFaqCategory;

      var line = detectionLine();
      var handle = sections[0].dataset.jdFaqCategory;

      sections.forEach(function (section) {
        if (section.getBoundingClientRect().top <= line) handle = section.dataset.jdFaqCategory;
      });

      return handle;
    }

    /* A tab click or a deep link scrolls across every category in between; letting them light up on
       the way is the flicker that made this tracking unwelcome the first time. So the target is set
       at once and the reading is held off until the scroll lands — or until the visitor scrolls
       themselves, which hands control straight back. */
    var held = false;
    var releaseTimer = null;

    function release() {
      held = false;
      if (releaseTimer) {
        clearTimeout(releaseTimer);
        releaseTimer = null;
      }
    }

    function holdDuring() {
      held = true;
      if (releaseTimer) clearTimeout(releaseTimer);
      releaseTimer = setTimeout(release, SCROLL_SETTLE_MS);
    }

    ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
      window.addEventListener(type, release, { passive: true });
    });

    var scheduled = false;

    function sync() {
      if (scheduled) return;
      scheduled = true;

      window.requestAnimationFrame(function () {
        scheduled = false;
        if (held) return;
        activate(handleInView());
      });
    }

    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    if ('onscrollend' in window) window.addEventListener('scrollend', release);

    sync();

    return { sync: sync, hold: holdDuring };
  }

  function init() {
    setupStickyOffset();

    var activate = setupTabs();
    if (!activate) return;

    var spy = setupScrollSpy(activate);

    function syncFromHash() {
      var handle = categoryHandleFromHash();

      /* The anchor scrolls and opens; the reading decides the tab. Setting the target here too only
         spares the visitor a tab that lags behind the scroll it just triggered. */
      if (handle) {
        if (spy) spy.hold();
        activate(handle);
      }

      revealFromHash();
    }

    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);

    /* Re-clicking the tab of the category already in the URL fires no `hashchange`, but the page
       still scrolls back to it — so the hold has to be armed from the click as well. */
    Array.prototype.slice.call(document.querySelectorAll('[data-jd-faq-tab]')).forEach(function (tab) {
      tab.addEventListener('click', function () {
        if (spy) spy.hold();
        activate(tab.dataset.jdFaqTab);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
